/**
 * Hooks para el MVP de Reportes Personalizados.
 *   - useCustomReportSchema(entity): registry de campos + filtros.
 *   - useGenerateCustomReport(): POST + descarga blob o 202 async.
 */
import { useQuery, useMutation } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

// ─── Types ───────────────────────────────────────────────────────────────────

export type ReportEntity = 'sales' | 'purchases'
export type ReportFormat = 'xlsx' | 'csv'

export interface CustomFieldSpec {
  key: string
  label: string
  group: string
  type: 'string' | 'number' | 'date' | 'datetime' | 'boolean'
  default_selected: boolean
}

export interface CustomFieldGroup {
  group: string
  fields: CustomFieldSpec[]
}

export interface OrderOption {
  key: string
  label: string
}

export interface CustomReportSchema {
  entity: ReportEntity
  label: string
  groups: CustomFieldGroup[]
  filters: string[]
  order_options: OrderOption[]
  sync_threshold: number
}

export interface CustomReportFilters {
  date_from?: string
  date_to?: string
  status?: string[]
  document_type?: string[]
  source?: string[]
  search?: string
  amount_min?: string
  amount_max?: string
  /** Ejecutivo comercial (id del vendedor del pedido origen). Solo ventas. */
  seller_id?: string
  /** Campo de orden con prefijo '-' para descendente. Ej: '-created_at'. */
  ordering?: string
}

export interface GenerateReportPayload {
  entity: ReportEntity
  fields: string[]
  filters?: CustomReportFilters
  format: ReportFormat
}

export interface GenerateReportAsyncResponse {
  status: 'queued'
  row_count: number
  message: string
}

export type GenerateReportResult =
  | { kind: 'sync'; blob: Blob; filename: string; rowCount: number }
  | { kind: 'async'; rowCount: number; message: string }

// ─── Hooks ───────────────────────────────────────────────────────────────────

export function useCustomReportSchema(entity: ReportEntity | null) {
  return useQuery({
    queryKey: ['custom-report-schema', entity],
    queryFn: async () => {
      const res = await apiClient.get(`/reports/custom/schema/`, {
        params: { entity },
      })
      return res.data as CustomReportSchema
    },
    enabled: !!entity,
    staleTime: 5 * 60 * 1000, // schema rara vez cambia
  })
}

function extractFilenameFromDisposition(disposition: string, fallback: string): string {
  const match = /filename="?([^";]+)"?/i.exec(disposition || '')
  return match ? match[1] : fallback
}

export function useGenerateCustomReport() {
  return useMutation<GenerateReportResult, Error, GenerateReportPayload>({
    mutationFn: async (payload) => {
      const res = await apiClient.post('/reports/custom/', payload, {
        responseType: 'blob',
        validateStatus: (s) => s === 200 || s === 202 || s === 400,
      })

      // Async path: 202 viene como JSON. El responseType=blob lo vuelve blob,
      // pero podemos leer como texto y parsear.
      if (res.status === 202) {
        const text = await (res.data as Blob).text()
        const parsed = JSON.parse(text) as GenerateReportAsyncResponse
        return {
          kind: 'async' as const,
          rowCount: parsed.row_count,
          message: parsed.message,
        }
      }

      // 400 (empty result, validation) → parsear y tirar error
      if (res.status === 400) {
        const text = await (res.data as Blob).text()
        let msg = 'Error al generar el reporte.'
        try {
          const parsed = JSON.parse(text)
          msg = parsed?.error?.message || msg
        } catch {
          // ignore
        }
        throw new Error(msg)
      }

      // Sync: 200 con archivo
      const disposition = res.headers['content-disposition'] || ''
      const filename = extractFilenameFromDisposition(
        disposition,
        `reporte_${payload.entity}_${Date.now()}.${payload.format}`,
      )
      const rowCount = parseInt(res.headers['x-row-count'] || '0', 10)
      return {
        kind: 'sync' as const,
        blob: res.data as Blob,
        filename,
        rowCount,
      }
    },
  })
}

export function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  setTimeout(() => {
    URL.revokeObjectURL(url)
    a.remove()
  }, 100)
}
